import { useEffect, useMemo, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { playScanBeep } from '../../utils/scanSounds';

/** Same-code debounce slightly longer than the post-success pause to avoid an immediate re-read. */
const SAME_CODE_DEBOUNCE_MS = 900;
const SUCCESS_PAUSE_MS = 700;

function pickRearCamera(cameras) {
  if (!cameras?.length) return null;
  const rear = cameras.find((c) => /back|rear|environment/i.test(c.label || ''));
  return rear?.id || cameras[cameras.length - 1]?.id || cameras[0]?.id;
}

function computeQrBox(viewfinderWidth, viewfinderHeight) {
  const width = Math.min(Math.floor(viewfinderWidth * 0.9), 360);
  const height = Math.min(Math.floor(viewfinderHeight * 0.55), 160);
  return {
    width: Math.max(width, 160),
    height: Math.max(height, 72),
  };
}

/**
 * Live barcode decode using device camera (mobile or laptop webcam).
 *
 * @param {(text: string) => boolean | void} onDecoded — Return `true` after a checklist match (beep, green overlay, brief scan pause). Return `false` when there is no match (brief error cue). Omit or return other values for legacy callers (no success treatment).
 */
function BarcodeCameraScanner({ onDecoded }) {
  const regionId = useMemo(() => `pack-bc-${Math.random().toString(36).slice(2)}`, []);
  const scannerRef = useRef(null);
  const lastRef = useRef({ code: '', t: 0 });
  const onDecodedRef = useRef(onDecoded);
  const scanPausedRef = useRef(false);
  const pausedCodeRef = useRef('');
  const feedbackTimersRef = useRef([]);
  const wakeLockRef = useRef(null);
  const [cameraError, setCameraError] = useState(null);
  const [regionState, setRegionState] = useState('starting');
  const [lastHint, setLastHint] = useState(null);
  onDecodedRef.current = onDecoded;

  useEffect(() => {
    const scanner = new Html5Qrcode(regionId, { verbose: false });
    scannerRef.current = scanner;

    const clearFeedbackTimers = () => {
      feedbackTimersRef.current.forEach(clearTimeout);
      feedbackTimersRef.current = [];
    };

    const config = {
      fps: 10,
      qrbox: computeQrBox,
      aspectRatio: 1.333333,
      formatsToSupport: [
        Html5QrcodeSupportedFormats.CODE_128,
        Html5QrcodeSupportedFormats.CODE_39,
        Html5QrcodeSupportedFormats.CODE_93,
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.UPC_A,
        Html5QrcodeSupportedFormats.UPC_E,
        Html5QrcodeSupportedFormats.ITF,
        Html5QrcodeSupportedFormats.CODABAR,
        Html5QrcodeSupportedFormats.RSS_14,
        Html5QrcodeSupportedFormats.RSS_EXPANDED,
        Html5QrcodeSupportedFormats.UPC_EAN_EXTENSION,
      ],
    };

    const debouncedDecode = (text) => {
      if (scanPausedRef.current) return;

      const trimmed = String(text || '').trim();
      if (!trimmed) return;

      const now = Date.now();
      const { code, t } = lastRef.current;
      if (trimmed === code && now - t < SAME_CODE_DEBOUNCE_MS) return;

      const handled = onDecodedRef.current?.(trimmed);
      lastRef.current = { code: trimmed, t: now };

      if (handled === true) {
        playScanBeep('success');
        clearFeedbackTimers();
        setRegionState('success');
        setLastHint({ code: trimmed, status: 'success' });
        scanPausedRef.current = true;
        pausedCodeRef.current = trimmed;
        feedbackTimersRef.current.push(
          setTimeout(() => {
            scanPausedRef.current = false;
            pausedCodeRef.current = '';
            setRegionState('idle');
          }, SUCCESS_PAUSE_MS),
        );
        return;
      }

      if (handled === false) {
        clearFeedbackTimers();
        setRegionState('nomatch');
        setLastHint({ code: trimmed, status: 'error' });
        feedbackTimersRef.current.push(setTimeout(() => setRegionState('idle'), 700));
      }
    };

    let cancelled = false;

    const requestWakeLock = async () => {
      try {
        if (navigator.wakeLock?.request) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch {
        // ignore
      }
    };

    scanner
      .start({ facingMode: 'environment' }, config, debouncedDecode, () => {})
      .then(() => {
        if (!cancelled) {
          setCameraError(null);
          setRegionState('idle');
          void requestWakeLock();
        }
      })
      .catch(async () => {
        try {
          const cameras = await Html5Qrcode.getCameras();
          const camId = pickRearCamera(cameras);
          if (!camId) throw new Error('No camera found');
          await scanner.start(camId, config, debouncedDecode, () => {});
          if (!cancelled) {
            setCameraError(null);
            setRegionState('idle');
            void requestWakeLock();
          }
        } catch (err) {
          if (!cancelled) {
            scannerRef.current = null;
            setRegionState('error');
            setCameraError(
              err?.message ||
                'Could not open camera. Allow permission or use the keyboard wedge field.',
            );
          }
        }
      });

    return () => {
      clearFeedbackTimers();
      scanPausedRef.current = false;
      pausedCodeRef.current = '';
      cancelled = true;
      wakeLockRef.current?.release?.().catch(() => {});
      wakeLockRef.current = null;
      const s = scannerRef.current;
      scannerRef.current = null;
      if (!s) return;
      s.stop()
        .then(() => s.clear())
        .catch(() => {});
    };
  }, [regionId]);

  const mountClass =
    'packv2-camera-mount' +
    (regionState === 'success' ? ' packv2-camera-mount--success' : '') +
    (regionState === 'nomatch' ? ' packv2-camera-mount--nomatch' : '') +
    (regionState === 'starting' ? ' packv2-camera-mount--starting' : '');

  const regionClass =
    'packv2-camera-region' +
    (regionState === 'success' ? ' packv2-camera-region--success' : '') +
    (regionState === 'nomatch' ? ' packv2-camera-region--nomatch' : '');

  return (
    <div className={mountClass}>
      <div id={regionId} className={regionClass} />
      {regionState === 'starting' && !cameraError ? (
        <p className="packv2-camera-hint">Starting camera…</p>
      ) : null}
      {cameraError ? (
        <p className="packv2-camera-error" role="alert">
          {cameraError}
        </p>
      ) : (
        <p className="packv2-camera-hint" aria-live="polite">
          {lastHint?.status === 'success'
            ? `Matched ${lastHint.code}`
            : lastHint?.status === 'error'
              ? `No match for ${lastHint.code}`
              : 'Point at an IKEA article barcode (1D only — not QR). Same checklist as the keyboard field.'}
        </p>
      )}
    </div>
  );
}

export default BarcodeCameraScanner;
